import {
  DeleteOutlined,
  MinusOutlined,
  PictureOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { App, Button, ConfigProvider, Select, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { VAT_RATE } from '../../constants';
import {
  calculateOrderSummary,
  formatCurrency,
  getEffectivePrice,
  getProductImageUrl,
} from '../../utils';
import KioskCheckoutModal from './KioskCheckoutModal';

const { Paragraph } = Typography;

const KioskCart = ({
  customer,
  order,
  cartItems,
  updateQuantity,
  onClose,
  clearCart,
  isClearingCart: isClearingCartProp = false,
  onPaymentProcessingChange,
  onCheckoutStateChange,
  guestInfo,
  onTerminalReferenceChange,
}) => {
  const { modal: antdModal } = App.useApp();
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  const showClearCartConfirm = () => {
    antdModal.confirm({
      title: 'Delete all items?',
      icon: <DeleteOutlined style={{ color: '#ef4444' }} />,
      content: 'Are you sure you want to remove all items from your cart?',
      okText: 'Yes, Delete all items',
      cancelText: 'Keep Items',
      centered: true,
      zIndex: 2500,
      maskClosable: false,
      okButtonProps: {
        danger: true,
        size: 'large',
      },
      cancelButtonProps: {
        size: 'large',
      },
      onOk: async () => {
        await clearCart();
      },
    });
  };

  const { subtotal, vat: taxTotal, grandTotal } = calculateOrderSummary(cartItems);

  useEffect(() => {
    if (onCheckoutStateChange) {
      onCheckoutStateChange(isCheckoutModalOpen);
    }
  }, [isCheckoutModalOpen, onCheckoutStateChange]);

  // Reset checkout modal if cart is emptied (e.g. on inactivity reset)
  useEffect(() => {
    if (cartItems.length === 0) {
      setIsCheckoutModalOpen(false);
    }
  }, [cartItems.length]);

  return (
    <div className="kiosk-cart-container">
      <div className="kiosk-cart-header">
        <h2 className="kiosk-cart-title">Cart</h2>
        <div className="kiosk-cart-header-actions">
          {cartItems.length > 0 && (
            <Button
              type="text"
              onClick={showClearCartConfirm}
              className="kiosk-cart-clear-btn"
            >
              Delete all items
            </Button>
          )}

        </div>
      </div>

      <div className="kiosk-cart-body">
        {cartItems.length === 0 ? (
          <div className="kiosk-empty-cart">Your cart is empty.</div>
        ) : (
          cartItems?.map((item) => {
            const itemId = item?.id;
            const qty = item.quantity === '' ? 1 : Number(item.quantity);
            const effectivePrice = getEffectivePrice(item);
            const totalPrice = effectivePrice * qty;
            const itemVat =
              item.taxable === 'Taxable' || item.vat ? totalPrice * VAT_RATE : 0;
            const productImage = getProductImageUrl(item);

            return (
              <div key={itemId} className="kiosk-cart-item">
                <div className="kiosk-item-image-wrapper">
                  {(item?.product_code || item?.code) && (
                    <div className="cart-item-code-badge" title={item?.product_code || item?.code}>
                      {item?.product_code || item?.code}
                    </div>
                  )}
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={item?.product_name || item?.name}
                      className="kiosk-item-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="kiosk-image-placeholder"
                    style={{ display: productImage ? 'none' : 'flex' }}
                  >
                    <PictureOutlined />
                  </div>
                </div>

                <div className="kiosk-item-details">
                  <div className="kiosk-item-name">
                    <Paragraph
                      ellipsis={{
                        rows: 1,
                        tooltip: true,
                      }}
                      style={{ margin: 0, color: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                    >
                      {item?.product_name || item?.name}
                    </Paragraph>
                  </div>
                  <div className="kiosk-item-price-row">
                    <div className="kiosk-item-price-wrap">
                      <span className="kiosk-item-price">
                        {formatCurrency(totalPrice)}
                      </span>
                      {(item.taxable === 'Taxable' || !!item.vat) && (
                        <span className="kiosk-item-vat-text">
                          +{formatCurrency(itemVat)} VAT
                        </span>
                      )}
                    </div>

                    <div className="kiosk-item-actions">
                      <ConfigProvider
                        theme={{
                          token: {
                            colorPrimary: '#F58220',
                            borderRadius: 24,
                            colorBorder: 'transparent',
                            colorBgContainer: '#f3f4f6',
                            controlHeight: 34,
                          },
                        }}
                      >
                        <Select
                          placeholder="UOM"
                          value={item?.uom || 'Case'}
                          onChange={(value) => updateQuantity(itemId, item.quantity, item, value)}
                          style={{ width: 80 }}
                          styles={{
                            root: {
                              borderRadius: 12,
                            },
                          }}
                          options={
                            item?.product_uom?.toLowerCase() === 'each' || item?.product_item_barcode
                              ? [
                                { value: 'Each', label: 'Each' },
                                { value: 'Case', label: 'Case', disabled: !item?.tiers?.some((tier) => tier.minimum_quantity > 1) },
                              ]
                              : [
                                { value: 'Case', label: 'Case', disabled: !item?.tiers?.some((tier) => tier.minimum_quantity > 1) },
                              ]
                          }
                        />
                      </ConfigProvider>
                      <div className="kiosk-quantity-controls">
                        <button
                          type="button"
                          className="kiosk-qty-button"
                          disabled={Number(item.quantity) <= 1}
                          onClick={() => {
                            const currentQty =
                              item.quantity === '' ? 1 : Number(item.quantity);
                            if (currentQty > 1) {
                              updateQuantity(itemId, currentQty - 1, item);
                            }
                          }}
                        >
                          <MinusOutlined className="kiosk-qty-icon" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          max="999"
                          className="kiosk-quantity-text"
                          style={{
                            border: 'none',
                            background: 'transparent',
                            outline: 'none',
                            padding: 0,
                            MozAppearance: 'textfield',
                          }}
                          value={item.quantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              updateQuantity(itemId, '', item);
                            } else {
                              const num = parseInt(val, 10);
                              if (!isNaN(num)) {
                                updateQuantity(
                                  itemId,
                                  Math.min(999, Math.max(1, num)),
                                  item
                                );
                              }
                            }
                          }}
                          onBlur={() => {
                            if (item.quantity === '' || Number(item.quantity) <= 0) {
                              updateQuantity(itemId, 1, item);
                            } else if (Number(item.quantity) > 999) {
                              updateQuantity(itemId, 999, item);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="kiosk-qty-button"
                          disabled={Number(item.quantity) >= 999}
                          onClick={() =>
                            updateQuantity(
                              itemId,
                              Math.min(
                                999,
                                (item.quantity === '' ? 0 : Number(item.quantity)) + 1
                              ),
                              item
                            )
                          }
                        >
                          <PlusOutlined className="kiosk-qty-icon" />
                        </button>
                      </div>

                      <button
                        type="button"
                        className="kiosk-item-delete-btn"
                        title="Remove item"
                        aria-label="Remove item"
                        onClick={() => updateQuantity(itemId, 0, item)}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="kiosk-cart-footer">
        <div className="kiosk-summary-container">
          <div className="kiosk-summary-row">
            <span className="kiosk-summary-label">Subtotal
              <span className="vat-label ">(Exc. VAT)</span>
            </span>
            <span className="kiosk-summary-value">{formatCurrency(subtotal)}</span>
          </div>
          <div className="kiosk-summary-row">
            <span className="kiosk-summary-label">VAT</span>
            <span className="kiosk-summary-value">{formatCurrency(taxTotal)}</span>
          </div>
          <div className="kiosk-summary-row kiosk-summary-row-total">
            <span className="kiosk-summary-total-label">Total
              <span className="vat-label">(Inc. VAT)</span>
            </span>
            <span className="kiosk-total-value">{formatCurrency(grandTotal)}</span>
          </div>
        </div>

        <button
          className="kiosk-checkout-button"
          disabled={cartItems.length === 0}
          onClick={() => setIsCheckoutModalOpen(true)}
        >
          Checkout
        </button>
      </div>

      {/* Checkout Order Review & Payment Modal Component */}
      <KioskCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        customer={customer}
        order={order}
        cartItems={cartItems}
        subtotal={subtotal}
        taxTotal={taxTotal}
        grandTotal={grandTotal}
        clearCart={clearCart}
        onPaymentProcessingChange={onPaymentProcessingChange}
        guestInfo={guestInfo}
        onTerminalReferenceChange={onTerminalReferenceChange}
      />

    </div>
  );
};

export default KioskCart;
